import React, { useState } from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { useCollectionProgress } from '@lootboy/lib/slices/cardCollections/collectionProgress';
import useCollectionGoals from '@lootboy/lib/slices/cardCollections/hooks/useCollectionGoals';
import useProgressBar from '@lootboy/lib/slices/cardCollections/hooks/useProgressBar';
import {
  ECollectionBannerState,
  ICollection,
} from '@lootboy/lib/slices/cardCollections/types';
import { areAllGoalsFulfilled } from '@lootboy/lib/slices/cardCollections/utils';

import StyledButton, {
  EStyledButtonVariant,
} from '~/components/atoms/StyledButton';
import ButtonClean from '~/components/ButtonClean';
import ContentView from '~/components/ContentView';
import RatingProgressBar from '~/components/molecules/RatingProgressBar';

import CollectionCardGrid from '~/views/CollectionView/CollectionCardGrid';
import { CollectionRankingWithDialogs } from '~/views/CollectionView/CollectionRanking/CollectionRankingWithDialogs';
import { CollectionGoals } from '~/views/CollectionView/FetchedCollectionContent/CollectionGoals';
import RemoveCollectionDialog from '~/views/CollectionView/RemoveCollectionDialog';
import SubmitCollectionDialog from '~/views/CollectionView/SubmitCollectionDialog';

import { ReactComponent as TrashIcon } from '~/assets/images/icons/i-trash.svg';

import styles from './FetchedCollectionContent.module.scss';

interface IFetchedCollectionContentProps {
  collection: ICollection;
  collectionState: ECollectionBannerState;
}

const FetchedCollectionContent: React.FC<IFetchedCollectionContentProps> = ({
  collection,
  collectionState,
}) => {
  const [isRemoveDialogShown, setRemoveDialogShown] = useState(false);
  const [isSubmitDialogShown, setSubmitDialogShown] = useState(false);
  const collectionProgress = useCollectionProgress(collection._id);
  const goals = useCollectionGoals(collection._id);
  const progress = useProgressBar(collection._id);

  const areGoalsFulfilled = goals && areAllGoalsFulfilled(goals);
  const isHidden = Boolean(collectionProgress?.progress?.hidden);

  return (
    <>
      {isRemoveDialogShown && (
        <RemoveCollectionDialog
          onClose={() => {
            setRemoveDialogShown(false);
          }}
          header={collection.headline}
          collectionId={collection._id}
        />
      )}
      {isSubmitDialogShown && (
        <SubmitCollectionDialog
          onClose={() => {
            setSubmitDialogShown(false);
          }}
          collectionId={collection._id}
          showCollectionProgress={Boolean(goals?.reachTargetValue?.isRequired)}
        />
      )}
      <section className={styles.cardSection}>
        {goals?.reachTargetValue?.isRequired && (
          <RatingProgressBar
            value={progress?.value ?? 0}
            target={goals.reachTargetValue.targetValue}
            targetMax={goals.reachTargetValue.targetValueMax}
            isUpcoming={collectionState === ECollectionBannerState.UPCOMING}
          />
        )}
        <CollectionCardGrid collectionId={collection._id} />
        <div className={styles.collectionCompletion}>
          <CollectionGoals goals={goals} />
          {collectionState !== ECollectionBannerState.EXPIRED &&
          collectionState !== ECollectionBannerState.COMPLETED ? (
            <StyledButton
              variant={EStyledButtonVariant.BLUE}
              className={styles.submitButton}
              disabled={!areGoalsFulfilled}
              onClick={() => setSubmitDialogShown(true)}
            >
              {I18n.t('collections_bs_submit_pbutton')}
            </StyledButton>
          ) : (
            <>
              <div className={classNames(styles.info, styles[collectionState])}>
                {collectionState === ECollectionBannerState.EXPIRED
                  ? I18n.t('collections_collection_ended')
                  : I18n.t('collections_collection_completed')}
              </div>
              {!isHidden && (
                <ButtonClean
                  className={styles.hideButton}
                  onClick={() => setRemoveDialogShown(true)}
                >
                  <TrashIcon />
                  {I18n.t('collections_hide')}
                </ButtonClean>
              )}
            </>
          )}
        </div>
      </section>
      {goals?.reachTargetValue?.isRequired &&
        Boolean(collection.rankingEnabled) && (
          <CollectionRankingWithDialogs
            collectionId={collection._id}
            collectionName={collection.headline}
            collectionValue={progress?.value ?? 0}
          />
        )}
      {Boolean(collection.content?.length) && (
        <section className={styles.contentSection}>
          {collection.content.map((content) => (
            <ContentView
              {...content}
              key={content._id}
            />
          ))}
        </section>
      )}
    </>
  );
};

export default FetchedCollectionContent;
